import { Briefcase, Calendar, Link2, Star } from 'lucide-react';
import { PRODUCT_IMAGES } from '../../../../../branding/assets.js';
import {
  IntegrationCard,
  ModeOptionList,
} from './AIConfigurationUI.tsx';
import type { ModeSelectOption } from './AIConfigurationUI.tsx';
import {
  BooleanToggle,
  Field,
  SectionCard,
  inputBase,
} from './FormPromptControls.tsx';

type ModoReservas = 'whatsapp' | 'link';
type ReservaLinkOrigem = 'reservo' | 'externo';

type AdditionalStepProps = {
  form: any;
  errors: Record<string, string>;
  reservasFeatureOn: boolean;
  showFeedbackPedidos: boolean;
  showVagas: boolean;
  modoReservas: ModoReservas | null;
  modoReservasDirty: boolean;
  reservasModeOptions: ModeSelectOption[];
  setModoReservas: (id: ModoReservas) => void;
  hasReservoProduct: boolean;
  reservaOrigemOptions: ModeSelectOption[];
  reservaOrigemEfetiva: ReservaLinkOrigem;
  setReservaLinkOrigem: (id: ReservaLinkOrigem) => void;
  markDirty: () => void;
  reservoPublicLink: string;
  reservaExternoLink: string;
  reservaLinkPreview: string;
  hasReservoWhatsappContractLink: boolean;
  reservoWhatsappContractUrl: string;
  hasFeedbackHeroProduct: boolean;
  hasFeedbackHeroWhatsappContractLink: boolean;
  feedbackHeroWhatsappContractUrl: string;
  hasCvProduct: boolean;
  usaCvVagas: boolean;
  cvLinkExample: string;
  cvPublicLink: string;
  openRelatedProduct: (path: string) => void;
  openProductCheckout: (product: string) => void;
  openExternalLink: (url: string) => void;
  updateField: (key: string, value: unknown) => void;
};

const AdditionalStep = ({
  form,
  errors,
  reservasFeatureOn,
  showFeedbackPedidos,
  showVagas,
  modoReservas,
  modoReservasDirty,
  reservasModeOptions,
  setModoReservas,
  hasReservoProduct,
  reservaOrigemOptions,
  reservaOrigemEfetiva,
  setReservaLinkOrigem,
  markDirty,
  reservoPublicLink,
  reservaExternoLink,
  reservaLinkPreview,
  hasReservoWhatsappContractLink,
  reservoWhatsappContractUrl,
  hasFeedbackHeroProduct,
  hasFeedbackHeroWhatsappContractLink,
  feedbackHeroWhatsappContractUrl,
  hasCvProduct,
  usaCvVagas,
  cvLinkExample,
  cvPublicLink,
  openRelatedProduct,
  openProductCheckout,
  openExternalLink,
  updateField,
}: AdditionalStepProps) => (
  <>
{reservasFeatureOn && (
  <SectionCard
    title="Reservas"
    icon={Calendar}
    description="Define se a IA registra a reserva na própria conversa (via Reservo) ou se envia um link para o cliente reservar."
    hint="Esta é a mesma escolha feita na etapa “O que a IA faz”. Se mudar aqui, muda lá também."
  >
    <div className="space-y-6">
      <div
        className={`max-w-3xl space-y-4${modoReservasDirty ? ' ai-inline-draft-block' : ''}`}
        data-error-key="modo_reservas"
      >
        {modoReservasDirty && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
            Rascunho
          </span>
        )}
        <ModeOptionList
          name="Como as reservas funcionam no seu restaurante?"
          size="large"
          options={reservasModeOptions}
          value={modoReservas}
          onSelect={(id) => setModoReservas(id as ModoReservas)}
        />
        {errors.modo_reservas && (
          <p className="text-sm font-semibold text-rose-600">{errors.modo_reservas}</p>
        )}
      </div>

      {modoReservas === 'whatsapp' && (
        <>
          <IntegrationCard
            logo={PRODUCT_IMAGES.reservoIcon}
            logoClassName="is-reservo"
            name="Reservo"
            status="connected"
            description="A IA consulta a disponibilidade no Reservo, valida a janela de reserva e registra a reserva na conversa."
            actionLabel="Configurar no Reservo"
            onAction={() => openRelatedProduct('/reservo')}
          />
          <Field
            label="Link alternativo de reservas (opcional)"
            helper="A IA prioriza a reserva na conversa e pode oferecer este link para quem preferir reservar sozinho."
            error={errors.link_reserva_externo}
            errorKey="link_reserva_externo"
          >
            <input
              className={inputBase}
              value={form.link_reserva_externo}
              onChange={(e) => updateField('link_reserva_externo', e.target.value)}
              placeholder="Ex.: https://meusistema.com/reservas"
            />
          </Field>
        </>
      )}

      {modoReservas === 'link' && hasReservoProduct && (
        <>
          <Field
            label="Qual link a IA envia?"
            helper="Com o Reservo ativo, o link público é gerado automaticamente. Você não precisa digitar nada."
            dirty={modoReservasDirty}
          >
            <ModeOptionList
              name="Origem do link de reservas"
              options={reservaOrigemOptions}
              value={reservaOrigemEfetiva}
              onSelect={(id) => {
                markDirty();
                setReservaLinkOrigem(id as ReservaLinkOrigem);
              }}
            />
          </Field>
          {reservaOrigemEfetiva === 'reservo' && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-xs font-semibold text-slate-700">Link que a IA vai enviar</p>
              <p className="mt-1 break-all text-xs text-slate-600">
                {reservoPublicLink || 'O link público aparece aqui automaticamente quando o restaurante estiver publicado no Reservo.'}
              </p>
            </div>
          )}
          {reservaOrigemEfetiva === 'reservo' && reservaExternoLink && (
            <p className="text-xs font-semibold text-amber-700">
              O link externo cadastrado deixa de ser enviado. Ao publicar, a IA passa a usar o link do Reservo.
            </p>
          )}
          {reservaOrigemEfetiva === 'externo' && (
            <Field
              label="Link de reservas"
              required
              helper="A IA envia exatamente este link quando o cliente quiser reservar."
              error={errors.link_reserva_externo}
              errorKey="link_reserva_externo"
            >
              <input
                className={inputBase}
                value={form.link_reserva_externo}
                onChange={(e) => updateField('link_reserva_externo', e.target.value)}
                placeholder="Ex.: https://meusistema.com/reservas"
              />
            </Field>
          )}
        </>
      )}

      {modoReservas === 'link' && !hasReservoProduct && (
        <>
          <Field
            label="Link de reservas"
            required
            helper="A IA envia exatamente este link quando o cliente quiser reservar."
            error={errors.link_reserva_externo}
            errorKey="link_reserva_externo"
          >
            <input
              className={inputBase}
              value={form.link_reserva_externo}
              onChange={(e) => updateField('link_reserva_externo', e.target.value)}
              placeholder="Ex.: https://meusistema.com/reservas"
            />
          </Field>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
            <p className="text-sm font-semibold text-amber-900">
              Ative o Reservo para liberar reservas pelo AtendaBot
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Com o Reservo, o link de reservas é gerado automaticamente e a IA pode registrar a reserva na própria conversa, com data, horários disponíveis e confirmação de dados.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openProductCheckout('reservo')}
                className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Ativar Reservo
              </button>
              <button
                type="button"
                disabled={!hasReservoWhatsappContractLink}
                onClick={() => openExternalLink(reservoWhatsappContractUrl)}
                className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {hasReservoWhatsappContractLink
                  ? 'Falar no WhatsApp'
                  : 'Falar no WhatsApp (pendente)'}
              </button>
            </div>
          </div>
        </>
      )}

      {modoReservas !== null && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4" aria-live="polite">
          <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Prévia da resposta</p>
          <p className="mt-2 text-sm text-slate-700">
            <strong>Cliente:</strong> “Queria uma mesa pra sexta à noite.”
          </p>
          <p className="mt-1 text-sm text-slate-700">
            <strong>IA:</strong>{' '}
            {modoReservas === 'whatsapp'
              ? '“Claro! Para quantas pessoas? Na sexta tenho horários às 19h e às 20h30.” A IA valida a disponibilidade e registra a reserva.'
              : `“Você pode reservar por aqui: ${reservaLinkPreview}. Qualquer dúvida, me chama!” A IA não registra reservas na conversa.`}
          </p>
        </div>
      )}
    </div>
  </SectionCard>
)}

{showFeedbackPedidos && (
  <SectionCard
    title="Feedback dos pedidos"
    icon={Star}
    description="Defina se o AtendaBot solicita avaliação por estrelas quando o pedido for finalizado."
  >
    <div className="space-y-6">
      <IntegrationCard
        logo={PRODUCT_IMAGES.feedbackHero}
        logoClassName="is-feedback-hero"
        name="FeedbackHero"
        status={hasFeedbackHeroProduct ? 'connected' : 'disconnected'}
        description="A IA envia uma avaliação por estrelas quando o pedido é concluído."
        actionLabel={hasFeedbackHeroProduct ? 'Configurar no FeedbackHero' : 'Ativar FeedbackHero'}
        onAction={() => hasFeedbackHeroProduct
          ? openRelatedProduct('/feedback-hero')
          : openProductCheckout('feedback_hero')}
      />
      {!hasFeedbackHeroProduct && hasFeedbackHeroWhatsappContractLink && (
        <button type="button" onClick={() => openExternalLink(feedbackHeroWhatsappContractUrl)} className="ai-button is-secondary">
          Falar no WhatsApp
        </button>
      )}

      {hasFeedbackHeroProduct && (
        <div className="grid gap-4 md:grid-cols-2">
          <Field
            label="Pedir avaliação de pedidos finalizados"
            helper="Ativa o envio automático de estrelas quando o pedido vira finalizado/entregue."
            error={errors.usar_feedbackhero_avaliacao_pedidos}
            errorKey="usar_feedbackhero_avaliacao_pedidos"
          >
            <BooleanToggle
              value={form.usar_feedbackhero_avaliacao_pedidos}
              onChange={(value) => updateField('usar_feedbackhero_avaliacao_pedidos', Boolean(value))}
            />
          </Field>
        </div>
      )}
    </div>
  </SectionCard>
)}

{showVagas && (
  <SectionCard
    title="Vagas"
    icon={Briefcase}
    description="Configure como a IA apresenta vagas abertas."
  >
    <div className="space-y-6">
      <IntegrationCard
        logo={PRODUCT_IMAGES.cv}
        logoClassName="is-cv"
        name="Currículo Certo"
        status={hasCvProduct ? 'connected' : 'disconnected'}
        description="A IA apresenta vagas abertas e envia o link de candidatura."
        actionLabel={hasCvProduct ? 'Configurar no Currículo Certo' : 'Ativar Currículo Certo'}
        onAction={() => hasCvProduct
          ? openRelatedProduct('/cv')
          : openProductCheckout('cv')}
      />
      {!hasCvProduct && <p className="text-xs text-slate-500">Exemplo do link de vagas: {cvLinkExample}</p>}

      {hasCvProduct && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="O AtendaBot deve responder sobre vagas?"
              helper="Ligado: quando alguém perguntar sobre emprego no WhatsApp, o AtendaBot apresenta as vagas e envia o link de candidatura."
              tooltip="O link das vagas é gerado automaticamente pelo Currículo Certo. Você não precisa criar nem configurar nada."
              error={errors.link_vagas}
              errorKey="link_vagas"
            >
              <BooleanToggle
                value={usaCvVagas}
                onChange={(value) => updateField('usar_cv_vagas', Boolean(value))}
              />
            </Field>
          </div>

          {usaCvVagas && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold text-slate-700">
                Link de vagas que o AtendaBot vai enviar
              </p>
              {cvPublicLink ? (
                <button
                  type="button"
                  onClick={() => openExternalLink(cvPublicLink)}
                  className="mt-1 inline-flex max-w-full items-center gap-2 text-left text-xs font-semibold text-teal-700 hover:text-teal-900"
                >
                  <Link2 size={14} className="flex-shrink-0" />
                  <span className="truncate">{cvPublicLink}</span>
                </button>
              ) : (
                <p className="mt-1 text-xs text-slate-600">
                  Crie suas vagas no Currículo Certo e este link aparece aqui automaticamente.
                </p>
              )}
              <p className="mt-2 text-[11px] text-slate-500">
                {cvPublicLink
                  ? 'Pronto! Esse link é criado e mantido atualizado sozinho, você não precisa fazer nada.'
                  : 'Assim que você publicar uma vaga, o link fica pronto automaticamente. Você não precisa configurar nada.'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  </SectionCard>
)}

  </>
);

export default AdditionalStep;
